import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  TableBar as TableBarIcon,
  ViewModule as BulkIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import TableActionButton from "../../components/layout/TableActionButton";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { RestaurantTable, tableAPI, TableStatus } from "../../services/tableAPI";
import { getApiErrorMessage } from "../../utils/apiError";
import { tokens } from "../../theme";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { sortRows } from "../../utils/sortRows";

const statusColor: Record<TableStatus, "success" | "warning" | "error" | "default"> = {
  AVAILABLE: "success",
  OCCUPIED: "warning",
  RESERVED: "default",
  BLOCKED: "error",
};

const TablesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { canCreate, canUpdate, canDelete, hasAnyRole } = useRBAC();
  const isOperator = hasAnyRole("OPERATOR");
  const canManageScope = hasAnyRole("SUPER_ADMIN", "ADMIN");

  const [branchId, setBranchId] = useState(user?.branchId ?? "");
  const [salesPointId, setSalesPointId] = useState(user?.salesPointId ?? "");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const { sortBy, sortOrder, handleSort } = useTableSort<
    "name" | "section" | "capacity" | "status"
  >("name", "asc");
  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(
    null
  );

  const [singleOpen, setSingleOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<RestaurantTable | null>(null);

  const [singleForm, setSingleForm] = useState({
    name: "",
    capacity: "4",
    section: "",
  });
  const [bulkForm, setBulkForm] = useState({
    prefix: "T",
    startNumber: "1",
    endNumber: "10",
    capacity: "4",
    section: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    capacity: "4",
    section: "",
  });

  useEffect(() => {
    if (!canManageScope && user) {
      if (user.branchId) setBranchId(user.branchId);
      if (user.salesPointId) setSalesPointId(user.salesPointId);
    }
  }, [canManageScope, user?.branchId, user?.salesPointId]);

  const { data: branches = [] } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
    enabled: canManageScope,
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["sales_points", branchId],
    queryFn: () => (branchId ? salesPointAPI.getSalesPointsByBranch(branchId) : []),
    enabled: canManageScope && !!branchId,
  });

  const {
    data: tablesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["manage-tables", branchId, salesPointId],
    queryFn: () =>
      tableAPI.getTables({
        branchId,
        salesPointId: salesPointId || undefined,
      }),
    enabled: !!branchId && !!salesPointId,
  });

  const tables = tablesData?.tables ?? [];
  const filteredTables = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.section ?? "").toLowerCase().includes(q)
    );
  }, [tables, search]);

  const sortedTables = useMemo(
    () =>
      sortRows(filteredTables, sortBy, sortOrder, (row, field) => {
        switch (field) {
          case "section":
            return row.section ?? "";
          case "capacity":
            return row.capacity;
          case "status":
            return row.status;
          default:
            return row.name;
        }
      }),
    [filteredTables, sortBy, sortOrder]
  );

  const onSort = (field: "name" | "section" | "capacity" | "status") =>
    handleSort(field, () => setPage(0));

  const pagedTables = sortedTables.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const salesPointName = useMemo(() => {
    if (isOperator) return "Your salesPoint";
    return salesPoints.find((d) => d.id === salesPointId)?.name ?? "Sales point";
  }, [salesPoints, salesPointId, isOperator]);

  const invalidateTables = () => {
    queryClient.invalidateQueries({ queryKey: ["manage-tables"] });
    queryClient.invalidateQueries({ queryKey: ["restaurant-tables"] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      tableAPI.createTable({
        branchId,
        salesPointId,
        name: singleForm.name.trim(),
        capacity: Number(singleForm.capacity) || 4,
        section: singleForm.section.trim() || null,
      }),
    onSuccess: () => {
      setAlert({ type: "success", message: "Table created" });
      setSingleOpen(false);
      setSingleForm({ name: "", capacity: "4", section: "" });
      invalidateTables();
    },
    onError: (err) =>
      setAlert({ type: "error", message: getApiErrorMessage(err, "Failed to create table") }),
  });

  const bulkMutation = useMutation({
    mutationFn: () =>
      tableAPI.bulkCreateTables({
        branchId,
        salesPointId,
        prefix: bulkForm.prefix.trim(),
        startNumber: Number(bulkForm.startNumber),
        endNumber: Number(bulkForm.endNumber),
        capacity: Number(bulkForm.capacity) || 4,
        section: bulkForm.section.trim() || null,
      }),
    onSuccess: (result) => {
      setAlert({
        type: "success",
        message: `${result.created.length} table(s) created${
          result.skipped.length ? `, ${result.skipped.length} skipped` : ""
        }`,
      });
      setBulkOpen(false);
      setBulkForm({ prefix: "T", startNumber: "1", endNumber: "10", capacity: "4", section: "" });
      invalidateTables();
    },
    onError: (err) =>
      setAlert({ type: "error", message: getApiErrorMessage(err, "Bulk create failed") }),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      tableAPI.updateTable(selected!.id, {
        name: editForm.name.trim(),
        capacity: Number(editForm.capacity) || 4,
        section: editForm.section.trim() || null,
      }),
    onSuccess: () => {
      setAlert({ type: "success", message: "Table updated" });
      setEditOpen(false);
      setSelected(null);
      invalidateTables();
    },
    onError: (err) =>
      setAlert({ type: "error", message: getApiErrorMessage(err, "Failed to update table") }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tableAPI.deleteTable(id),
    onSuccess: () => {
      setAlert({ type: "success", message: "Table deleted" });
      setDeleteOpen(false);
      setSelected(null);
      invalidateTables();
    },
    onError: (err) =>
      setAlert({ type: "error", message: getApiErrorMessage(err, "Failed to delete table") }),
  });

  const openEdit = (table: RestaurantTable) => {
    setSelected(table);
    setEditForm({
      name: table.name,
      capacity: String(table.capacity),
      section: table.section ?? "",
    });
    setEditOpen(true);
  };

  if (!branchId || !salesPointId) {
    return (
      <Box>
        <PageHeader title="Table Setup" subtitle="Manage restaurant dining tables" />
        <Alert severity="warning" sx={{ mt: 2 }}>
          Your account needs branch and salesPoint assigned before you can manage tables.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={isOperator ? "SalesPoint Tables" : "Table Setup"}
        subtitle={
          isOperator
            ? "Add and manage tables for your salesPoint"
            : "Create and manage restaurant tables by branch and salesPoint"
        }
        action={
          <Stack direction="row" spacing={1}>
            {canCreate("tables") && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<BulkIcon />}
                  onClick={() => setBulkOpen(true)}
                >
                  Bulk add
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setSingleOpen(true)}
                >
                  Add table
                </Button>
              </>
            )}
          </Stack>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getApiErrorMessage(error, "Failed to load tables")}
        </Alert>
      )}

      <DataListCard
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        searchPlaceholder="Search table name or section..."
        filters={
          canManageScope ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={branchId}
                  onChange={(e) => {
                    setBranchId(e.target.value);
                    setSalesPointId("");
                    setPage(0);
                  }}
                >
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }} disabled={!branchId}>
                <InputLabel>SalesPoint</InputLabel>
                <Select
                  label="Sales point"
                  value={salesPointId}
                  onChange={(e) => {
                    setSalesPointId(e.target.value);
                    setPage(0);
                  }}
                >
                  {salesPoints.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          ) : (
            <Chip
              icon={<TableBarIcon />}
              label={salesPointName}
              sx={{ bgcolor: alphaChip(tokens.colors.dashBlue) }}
            />
          )
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableTableCell label="Name" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Section" field="section" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Seats" field="capacity" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                <SortableTableCell label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    Loading tables...
                  </TableCell>
                </TableRow>
              ) : pagedTables.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No tables yet. Use Add table or Bulk add to create tables for this
                      salesPoint.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedTables.map((table) => (
                  <TableRow key={table.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{table.name}</TableCell>
                    <TableCell>{table.section || "General"}</TableCell>
                    <TableCell align="right">{table.capacity}</TableCell>
                    <TableCell>
                      <Chip
                        label={table.status}
                        size="small"
                        color={statusColor[table.status]}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canUpdate("tables") && (
                        <TableActionButton
                          title="Edit"
                          variant="primary"
                          onClick={() => openEdit(table)}
                        >
                          <EditIcon fontSize="small" />
                        </TableActionButton>
                      )}
                      {canDelete("tables") && table.status !== "OCCUPIED" && (
                        <TableActionButton
                          title="Delete"
                          variant="danger"
                          onClick={() => {
                            setSelected(table);
                            setDeleteOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </TableActionButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
              count={sortedTables.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </DataListCard>

      <Dialog open={singleOpen} onClose={() => setSingleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add table</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Table name"
              placeholder="T1, Table 5"
              value={singleForm.name}
              onChange={(e) => setSingleForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Seats"
              type="number"
              inputProps={{ min: 1 }}
              value={singleForm.capacity}
              onChange={(e) => setSingleForm((f) => ({ ...f, capacity: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Section / area"
              placeholder="AC, Rooftop"
              value={singleForm.section}
              onChange={(e) => setSingleForm((f) => ({ ...f, section: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!singleForm.name.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Bulk add tables</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Prefix"
                value={bulkForm.prefix}
                onChange={(e) => setBulkForm((f) => ({ ...f, prefix: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="From"
                type="number"
                inputProps={{ min: 1 }}
                value={bulkForm.startNumber}
                onChange={(e) =>
                  setBulkForm((f) => ({ ...f, startNumber: e.target.value }))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="To"
                type="number"
                inputProps={{ min: 1 }}
                value={bulkForm.endNumber}
                onChange={(e) => setBulkForm((f) => ({ ...f, endNumber: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Seats"
                type="number"
                inputProps={{ min: 1 }}
                value={bulkForm.capacity}
                onChange={(e) => setBulkForm((f) => ({ ...f, capacity: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Section / area"
                value={bulkForm.section}
                onChange={(e) => setBulkForm((f) => ({ ...f, section: e.target.value }))}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!bulkForm.prefix.trim() || bulkMutation.isPending}
            onClick={() => bulkMutation.mutate()}
          >
            Create range
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit table</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Table name"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Seats"
              type="number"
              inputProps={{ min: 1 }}
              value={editForm.capacity}
              onChange={(e) => setEditForm((f) => ({ ...f, capacity: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Section / area"
              value={editForm.section}
              onChange={(e) => setEditForm((f) => ({ ...f, section: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!editForm.name.trim() || updateMutation.isPending}
            onClick={() => updateMutation.mutate()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete table?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{selected?.name}</strong>? Occupied tables cannot be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => selected && deleteMutation.mutate(selected.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function alphaChip(color: string) {
  return `${color}22`;
}

export default TablesPage;
